import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  DollarSign,
  ExternalLink,
  Heart,
  IndianRupee,
  Loader,
  ShieldCheck,
} from "lucide-react";
import useRazorpay from "../../../hooks/useRazorpay";
import { useRegionalExperience } from "../../../hooks/useRegionalExperience";
import { getRegionExperience, missionSiteFallback } from "../../../data/missionContent";
import PaymentLogos from "../PaymentLogos";
import Field from "./Field";
import AmountSelector from "./AmountSelector";
import { donationResolver, MIN_AMOUNT, parseAmount } from "./donationSchema";

const SUBMIT_TIMEOUT_MS = 20000;
const EVERY_ORG_CARD_URL =
  "https://www.every.org/avdheshanandg-mission?search_meta=%7B%22query%22%3A%22avd%22%7D&donateTo=avdheshanandg-mission#/donate/card";

// Lightweight GA4/GTM funnel breadcrumbs for the submit lifecycle. Best-effort,
// never blocks a donation. Sits alongside the existing `purchase` event.
function pushFunnelEvent(step, payload = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: `donate_${step}`, ...payload });
}

const DEFAULT_CAMPAIGN_IMAGE = "/assets/agm/polyclinic.jpg";
const MONGO_OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;
const donateApiUrl = import.meta.env.VITE_APP_DONATE_API_URL?.replace(/\/$/, "");

const fallbackCampaign = {
  id: "general",
  title: "General AGM Seva Fund",
  description:
    "Support AGM’s active work across healthcare, future skilling, school education, women’s dignity, Mahakumbh seva and Vedantic education.",
  additionalText:
    "Your donation helps the mission respond where the need is most urgent while keeping the work rooted in dignity and accountability.",
  achieved: 0,
  goal: 0,
  donors: 0,
  daysLeft: 0,
  totalDays: 0,
  backgroundImage: DEFAULT_CAMPAIGN_IMAGE,
  bulletPoints: [],
  isActive: true,
};

const fallbackProgramCampaigns = missionSiteFallback.programPillars.slice(0, 5).map((program) => ({
  id: program.id,
  title: program.title,
  description: program.description,
  additionalText: program.eyebrow,
  achieved: 0,
  goal: 0,
  donors: 0,
  daysLeft: 0,
  totalDays: 0,
  backgroundImage: program.image || DEFAULT_CAMPAIGN_IMAGE,
  bulletPoints: program.metrics || [],
  isActive: true,
}));

const fallbackCampaignAliases = {
  champawat: "school",
  ifs: "future-skilling",
  institute: "future-skilling",
  "future-skilling": "future-skilling",
  healthcare: "healthcare",
  mahakumbh: "mahakumbh",
  "mass-wedding": "mass-wedding",
};

function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : fallback;
}

function formatNumber(value) {
  return new Intl.NumberFormat("en-IN").format(toNumber(value));
}

function formatAmount(value, prefix, fallback) {
  const amount = toNumber(value);
  return amount > 0 ? `${prefix}${formatNumber(amount)}` : fallback;
}

function toStringArray(value) {
  if (Array.isArray(value)) {
    return value
      .map(String)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function getFallbackCampaign(campaignId) {
  if (!campaignId) return fallbackCampaign;

  const slug = String(campaignId).trim().toLowerCase();
  const canonicalSlug = fallbackCampaignAliases[slug] || slug;

  return (
    fallbackProgramCampaigns.find((campaign) => campaign.id === canonicalSlug) || fallbackCampaign
  );
}

function isMongoObjectId(value) {
  return MONGO_OBJECT_ID_PATTERN.test(String(value || "").trim());
}

function getDaysLeft(campaign) {
  if (campaign.isActive === false) return 0;

  const providedDaysLeft = campaign.daysLeft;
  if (providedDaysLeft !== undefined && providedDaysLeft !== null) {
    return toNumber(providedDaysLeft);
  }

  const totalDays = toNumber(campaign.totalDays);
  if (!totalDays || !campaign.createdAt) return 0;

  const createdDate = new Date(campaign.createdAt);
  if (Number.isNaN(createdDate.getTime())) return 0;

  const endDate = new Date(createdDate);
  endDate.setDate(endDate.getDate() + totalDays);

  return Math.max(0, Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24)));
}

function normalizeCampaignDetails(campaign, fallback = fallbackCampaign) {
  if (!campaign || typeof campaign !== "object") return fallback;

  return {
    id: String(campaign._id || campaign.id || fallback.id),
    title: String(campaign.title || fallback.title),
    description: String(campaign.description || fallback.description),
    additionalText: String(campaign.additionalText || fallback.additionalText || ""),
    achieved: toNumber(campaign.achieved, fallback.achieved),
    goal: toNumber(campaign.goal, fallback.goal),
    donors: toNumber(campaign.donors, fallback.donors),
    daysLeft: getDaysLeft(campaign),
    totalDays: toNumber(campaign.totalDays, fallback.totalDays),
    backgroundImage: campaign.backgroundImage || fallback.backgroundImage || DEFAULT_CAMPAIGN_IMAGE,
    bulletPoints: toStringArray(campaign.bulletPoints),
    gallery: Array.isArray(campaign.gallery)
      ? campaign.gallery
          .filter((item) => item && typeof item === "object" && item.url)
          .map((item) => ({ url: String(item.url), caption: String(item.caption || "") }))
      : [],
    isActive: campaign.isActive !== false,
  };
}

function getApiBase() {
  return (
    import.meta.env.VITE_APP_RAZOR_DONATE_API_URL ||
    import.meta.env.VITE_APP_BASE_URL ||
    import.meta.env.VITE_API_BASE_URL ||
    "/api/nextjs"
  ).replace(/\/$/, "");
}

function getStripePublicKey() {
  return (
    import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  );
}

// --- Conversion tracking (GTM dataLayer) — best-effort, never blocks a donation ---
function purchaseItemId(campaignId) {
  return campaignId ? `donation_${campaignId}` : "donation_general_seva";
}

// Fire a GA4-style `purchase` event into the GTM dataLayer (used for rails where the
// amount is known client-side, i.e. Razorpay handler callbacks).
function pushPurchaseEvent({ transactionId, value, currency, donationType, itemId, itemName }) {
  if (typeof window === "undefined") return;
  const amount = Number(value) || 0;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "purchase",
    transaction_id: transactionId,
    value: amount,
    currency,
    donation_type: donationType,
    items: [
      {
        item_id: itemId || "donation_general_seva",
        item_name: itemName || "AGM Seva",
        price: amount,
        quantity: 1,
      },
    ],
  });
}

const DonationForm = ({ campaignId: propCampaignId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const routeParams = useParams();
  const queryParams = new URLSearchParams(location.search);
  const queryParamCampaignId = queryParams.get("campaignId") || queryParams.get("campaign");
  const queryRegion = queryParams.get("region")?.toUpperCase();
  const campaignId = propCampaignId || routeParams.campaignId || queryParamCampaignId;
  const isRazorpayLoaded = useRazorpay();
  const { siteConfig, regionKey, switchRegion } = useRegionalExperience();
  const [activeRegion, setActiveRegion] = useState(
    queryRegion === "US" ? "US" : queryRegion === "IN" ? "IN" : regionKey
  );
  const [isLoading, setIsLoading] = useState(Boolean(campaignId));
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(fallbackCampaign);
  const [claim80G, setClaim80G] = useState("no");
  const [lightbox, setLightbox] = useState(null); // { url, caption } | null
  const [statusMessage, setStatusMessage] = useState(""); // polite live-region text
  const [usFallbackUrl, setUsFallbackUrl] = useState(null); // explicit Every.org CTA target

  // Double-submit guard (synchronous, beats React state flush) + abort-on-unmount.
  const submitLockRef = useRef(false);
  const abortRef = useRef(null);

  const [givingMode, setGivingMode] = useState("one-time");
  const isIndia = activeRegion === "IN";
  const activeRegionConfig = getRegionExperience(siteConfig, activeRegion);
  const currency = isIndia ? "INR" : "USD";
  const amountPrefix = isIndia ? "₹" : "$";
  const FALLBACK_TIERS_IN = [
    { amount: 2100, impactLine: "", highlighted: false },
    { amount: 5100, impactLine: "", highlighted: true },
    { amount: 11000, impactLine: "", highlighted: false },
    { amount: 51000, impactLine: "", highlighted: false },
  ];
  const FALLBACK_TIERS_US = [
    { amount: 51, impactLine: "", highlighted: false },
    { amount: 108, impactLine: "", highlighted: true },
    { amount: 501, impactLine: "", highlighted: false },
    { amount: 1008, impactLine: "", highlighted: false },
  ];
  const FALLBACK_MONTHLY_TIERS_IN = [
    { amount: 500, impactLine: "", highlighted: false },
    { amount: 1100, impactLine: "", highlighted: false },
    { amount: 2100, impactLine: "", highlighted: false },
  ];
  const FALLBACK_MONTHLY_TIERS_US = [
    { amount: 15, impactLine: "", highlighted: false },
    { amount: 30, impactLine: "", highlighted: false },
    { amount: 60, impactLine: "", highlighted: false },
  ];
  const donationTiers =
    Array.isArray(activeRegionConfig?.donationTiers) && activeRegionConfig.donationTiers.length > 0
      ? activeRegionConfig.donationTiers
      : isIndia
        ? FALLBACK_TIERS_IN
        : FALLBACK_TIERS_US;
  const monthlyTiers =
    Array.isArray(activeRegionConfig?.monthlyTiers) && activeRegionConfig.monthlyTiers.length > 0
      ? activeRegionConfig.monthlyTiers
      : isIndia
        ? FALLBACK_MONTHLY_TIERS_IN
        : FALLBACK_MONTHLY_TIERS_US;
  const activeTiers = givingMode === "monthly" ? monthlyTiers : donationTiers;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onTouched", // field-level validation on blur, plus on submit
    shouldFocusError: true, // move focus to the first invalid field on submit
    resolver: donationResolver,
    context: { region: activeRegion, claim80G: isIndia && claim80G === "yes" },
    defaultValues: {
      amount: "",
      fullName: "",
      mobile: "",
      email: "",
      address: "",
      pan: "",
      termsAccepted: false,
    },
  });

  const watchAmount = watch("amount");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Abort any in-flight submit request if the donor navigates away mid-submit.
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (queryRegion === "US" || queryRegion === "IN") return;
    setActiveRegion(regionKey);
  }, [queryRegion, regionKey]);

  useEffect(() => {
    if (claim80G === "no") setValue("pan", "");
  }, [claim80G, setValue]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (event) => {
      if (event.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  useEffect(() => {
    let cancelled = false;

    async function fetchCampaignDetails() {
      if (!campaignId) {
        setCampaignDetails(fallbackCampaign);
        setError(null);
        setIsLoading(false);
        return;
      }

      const fallbackForCampaign = getFallbackCampaign(campaignId);

      if (!isMongoObjectId(campaignId)) {
        setCampaignDetails(fallbackForCampaign);
        setError(
          fallbackForCampaign.id === fallbackCampaign.id
            ? "This campaign link is not available in the live campaign list. The general seva fund is shown."
            : null
        );
        setIsLoading(false);
        return;
      }

      if (!donateApiUrl) {
        setCampaignDetails(fallbackForCampaign);
        setError(
          "Live campaign details are not configured here, so the general seva fund is shown."
        );
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await axios.get(`${donateApiUrl}/${encodeURIComponent(campaignId)}`);
        const campaign = response.data?.data ?? response.data?.campaign ?? response.data;

        if (!campaign || campaign.isActive === false || campaign.isDeleted === true) {
          throw new Error("Campaign not found");
        }

        if (!cancelled) {
          setCampaignDetails(normalizeCampaignDetails(campaign, fallbackForCampaign));
        }
      } catch {
        if (!cancelled) {
          setError("We could not load this campaign, so the general seva fund is shown.");
          setCampaignDetails(fallbackCampaign);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchCampaignDetails();

    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  const regionCopy = useMemo(() => {
    if (isIndia) {
      return {
        title: t("donateForm.regionCopy.india.title"),
        subtitle: t("donateForm.regionCopy.india.subtitle"),
        provider: "Razorpay",
        icon: IndianRupee,
      };
    }

    return {
      title: t("donateForm.regionCopy.usa.title"),
      subtitle: t("donateForm.regionCopy.usa.subtitle"),
      provider: "Stripe",
      icon: DollarSign,
    };
  }, [isIndia, t]);

  const selectRegion = (nextRegion) => {
    setActiveRegion(nextRegion);
    switchRegion(nextRegion);
    setValue("amount", "");
  };

  const buildDonationData = (data) => ({
    ...data,
    campaignId: campaignDetails.id,
    amount: Number(data.amount),
    currency,
    nationality: isIndia ? "Indian" : "US/International",
    tax80G: isIndia && claim80G === "yes",
    termsAccepted: Boolean(data.termsAccepted),
  });

  const processIndiaDonation = async (donationData, signal) => {
    if (!isRazorpayLoaded) {
      throw new Error("Payment is still loading. Please try again in a moment.");
    }

    const response = await axios.post(`${getApiBase()}/create-checkout-session`, donationData, {
      timeout: SUBMIT_TIMEOUT_MS,
      signal,
    });

    if (!response.data?.orderId) {
      throw new Error("We couldn't start the payment. Please try again.");
    }

    const options = {
      key: import.meta.env.VITE_APP_RAZORPAY_KEY_ID,
      amount: donationData.amount * 100,
      currency: "INR",
      name: "AvdheshanandG Mission",
      description: campaignDetails.title,
      order_id: response.data.orderId,
      prefill: {
        name: donationData.fullName,
        email: donationData.email,
        contact: donationData.mobile,
      },
      notes: {
        campaignId: donationData.campaignId,
        tax80G: donationData.tax80G ? "yes" : "no",
      },
      theme: { color: "#9d1f1c" },
      handler: async function (paymentResponse) {
        try {
          const verifyRes = await axios.post(`${getApiBase()}/verify-session`, paymentResponse);

          if (verifyRes.data.success) {
            pushPurchaseEvent({
              transactionId: paymentResponse.razorpay_payment_id,
              value: donationData.amount,
              currency: "INR",
              donationType: "one_time",
              itemId: purchaseItemId(donationData.campaignId),
              itemName: campaignDetails.title,
            });
            const params = new URLSearchParams({ provider: "razorpay" });
            if (verifyRes.data.receiptNumber) params.set("receipt", verifyRes.data.receiptNumber);
            if (verifyRes.data.receiptToken) params.set("token", verifyRes.data.receiptToken);
            navigate(`/success-payment?${params.toString()}`);
          } else {
            navigate("/decline-payment");
          }
        } catch {
          navigate("/decline-payment");
        }
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const processUsDonation = async (donationData, signal) => {
    const stripeKey = getStripePublicKey();

    // Stripe not configured for this environment → surface an explicit,
    // donor-initiated Every.org fallback instead of a silent mid-submit redirect.
    if (!stripeKey) {
      return { fallback: "everyorg" };
    }

    const stripe = await loadStripe(stripeKey);
    if (!stripe) throw new Error("Card checkout could not start. Please try again.");

    const response = await axios.post(
      `${getApiBase()}/auth/firebase/create-checkout-session`,
      {
        amount: donationData.amount,
        campaignId: donationData.campaignId,
        pan: donationData.pan || "",
        tax80G: donationData.tax80G,
        userInfo: {
          fullName: donationData.fullName,
          email: donationData.email,
          mobile: donationData.mobile,
          address: donationData.address,
          nationality: donationData.nationality,
        },
      },
      { timeout: SUBMIT_TIMEOUT_MS, signal }
    );

    if (!response.data?.id) {
      throw new Error("We couldn't start the secure checkout. Please try again.");
    }

    const result = await stripe.redirectToCheckout({ sessionId: response.data.id });
    if (result.error) {
      throw new Error(result.error.message || "Card checkout failed. Please try again.");
    }
    return { fallback: null };
  };

  const processMonthlySubscription = async (donationData, signal) => {
    if (isIndia && !isRazorpayLoaded) {
      throw new Error("Payment is still loading. Please try again in a moment.");
    }

    const response = await axios.post(
      `${getApiBase()}/donations/create-subscription`,
      {
        amount: donationData.amount,
        region: activeRegion,
        campaignId: donationData.campaignId,
        name: donationData.fullName,
        email: donationData.email,
        mobile: donationData.mobile,
        address: donationData.address,
        nationality: donationData.nationality,
        pan: donationData.pan || "",
        tax80G: donationData.tax80G,
      },
      { timeout: SUBMIT_TIMEOUT_MS, signal }
    );

    if (response.data?.provider === "razorpay") {
      if (!response.data?.subscriptionId) {
        throw new Error("Failed to start monthly subscription");
      }

      const options = {
        key: response.data.key || import.meta.env.VITE_APP_RAZORPAY_KEY_ID,
        subscription_id: response.data.subscriptionId,
        name: "AvdheshanandG Mission",
        description: `Monthly seva — ${campaignDetails.title}`,
        prefill: {
          name: donationData.fullName,
          email: donationData.email,
          contact: donationData.mobile,
        },
        notes: {
          campaignId: donationData.campaignId,
          recurring: "true",
        },
        theme: { color: "#9d1f1c" },
        handler: function (paymentResponse) {
          pushPurchaseEvent({
            transactionId:
              (paymentResponse && paymentResponse.razorpay_payment_id) ||
              response.data.subscriptionId,
            value: donationData.amount,
            currency: "INR",
            donationType: "recurring",
            itemId: purchaseItemId(donationData.campaignId),
            itemName: campaignDetails.title,
          });
          // First charge is recorded + receipted server-side via the
          // razorpay-subscription-webhook (subscription.charged). No verify-session here.
          navigate("/success-payment?type=subscription");
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      return;
    }

    const stripeKey = getStripePublicKey();
    if (!stripeKey) {
      setError("Monthly giving is temporarily unavailable.");
      return;
    }
    if (!response.data?.id) {
      throw new Error("We couldn't start monthly giving. Please try again.");
    }
    const stripe = await loadStripe(stripeKey);
    if (!stripe) throw new Error("Card checkout could not start. Please try again.");
    const result = await stripe.redirectToCheckout({ sessionId: response.data.id });
    if (result.error) {
      throw new Error(result.error.message || "Monthly card checkout failed. Please try again.");
    }
    return { fallback: null };
  };

  const onSubmit = async (data) => {
    if (submitLockRef.current) return; // synchronous double-submit guard
    submitLockRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    const rail = givingMode === "monthly" ? "subscription" : isIndia ? "razorpay" : "stripe";
    const donationType = givingMode === "monthly" ? "recurring" : "one_time";

    setIsProcessing(true);
    setError(null);
    setUsFallbackUrl(null);
    setStatusMessage(t("donateForm.processingStatus"));
    pushFunnelEvent("submit", { region: activeRegion, rail, donation_type: donationType });

    try {
      const donationData = buildDonationData(data);
      let result;

      if (givingMode === "monthly") {
        result = await processMonthlySubscription(donationData, controller.signal);
      } else if (isIndia) {
        result = await processIndiaDonation(donationData, controller.signal);
      } else {
        result = await processUsDonation(donationData, controller.signal);
      }

      if (result?.fallback === "everyorg") {
        setUsFallbackUrl(EVERY_ORG_CARD_URL);
        setStatusMessage("");
        pushFunnelEvent("fallback", { region: activeRegion, rail: "everyorg" });
      }
    } catch (err) {
      // Aborted (donor navigated away / unmounted) is not a user-facing failure.
      if (axios.isCancel?.(err) || err?.name === "CanceledError" || err?.name === "AbortError") {
        return;
      }
      const message =
        err?.code === "ECONNABORTED"
          ? "The request timed out. Please check your connection and try again."
          : err?.message || "Unable to process your donation. Please try again.";
      setError(message);
      setStatusMessage("");
      pushFunnelEvent("error", { region: activeRegion, rail, message });
      Sentry.captureException(err, { tags: { flow: "donate", rail, region: activeRegion } });
    } finally {
      submitLockRef.current = false;
      abortRef.current = null;
      setIsProcessing(false);
    }
  };

  const ProviderIcon = regionCopy.icon;

  return (
    <main className="overflow-x-hidden bg-[#f7faf9] text-[#14201f]">
      <section className="relative min-h-[54vh] overflow-hidden bg-[#2a120d] pt-24 text-white lg:pt-28">
        <img
          src={activeRegionConfig.bannerImage}
          alt={`${activeRegionConfig.label} giving experience`}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2a120d] via-[#35170f]/76 to-[#4b2418]/25" />
        <div className="container relative z-10 mx-auto flex min-h-[calc(54vh-7rem)] flex-col justify-end px-4 pb-12 sm:px-6 lg:px-8">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-[#f5c45e]">
            {regionCopy.title}
          </p>
          <h1 className="max-w-4xl text-4xl font-semibold leading-tight md:text-6xl">
            {t("donateForm.supportCampaign")} {campaignDetails.title}
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-white/82">
            {regionCopy.subtitle} {t("donateForm.switchRegionHint")}
          </p>
        </div>
      </section>

      <section className="container mx-auto px-4 py-14 sm:px-6 lg:px-8">
        {isLoading ? (
          <div className="flex min-h-96 items-center justify-center">
            <Loader className="mr-3 h-7 w-7 animate-spin text-[#9d1f1c]" />
            <span className="font-medium text-[#52615f]">{t("donateForm.loadingCampaign")}</span>
          </div>
        ) : (
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div className="space-y-7">
              {/* Screen-reader live region for submit status (polite). */}
              <p className="sr-only" role="status" aria-live="polite">
                {statusMessage}
              </p>

              {error && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="flex items-start gap-2 rounded-lg border border-[#f2d1d1] bg-[#fff5f5] p-4 text-sm text-[#9d1f1c]"
                >
                  <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}

              {usFallbackUrl && (
                <div className="rounded-lg border border-[#dfe8e6] bg-[#f7faf9] p-4 text-sm text-[#33413f]">
                  <p className="font-semibold text-[#14201f]">{t("donateForm.usFallback.heading")}</p>
                  <p className="mt-1 leading-6 text-[#52615f]">
                    {t("donateForm.usFallback.description")}
                  </p>
                  <a
                    href={usFallbackUrl}
                    className="mt-3 inline-flex items-center gap-2 rounded-md bg-[#9d1f1c] px-4 py-2.5 text-sm font-bold uppercase tracking-[0.12em] text-white transition hover:bg-[#7f1916]"
                  >
                    {t("donateForm.usFallback.button")}
                    <ExternalLink size={16} aria-hidden="true" />
                  </a>
                </div>
              )}

              <div className="overflow-hidden rounded-lg border border-[#dfe8e6] bg-white shadow-sm">
                <img
                  src={campaignDetails.backgroundImage || DEFAULT_CAMPAIGN_IMAGE}
                  alt={campaignDetails.title}
                  className="aspect-[16/10] w-full object-cover"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = DEFAULT_CAMPAIGN_IMAGE;
                  }}
                />
                <div className="p-7">
                  <h2 className="text-3xl font-semibold text-[#14201f]">{campaignDetails.title}</h2>
                  <p className="mt-4 text-base leading-8 text-[#52615f]">
                    {campaignDetails.description}
                  </p>
                  {campaignDetails.additionalText && (
                    <p className="mt-4 text-base leading-8 text-[#52615f]">
                      {campaignDetails.additionalText}
                    </p>
                  )}
                  {campaignDetails.bulletPoints?.length > 0 && (
                    <ul className="mt-6 space-y-3">
                      {campaignDetails.bulletPoints.slice(0, 5).map((point, index) => (
                        <li
                          key={`${point}-${index}`}
                          className="flex gap-3 text-sm leading-6 text-[#33413f]"
                        >
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#9d1f1c]" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-7 grid grid-cols-2 gap-4">
                    <div className="border-t border-[#dfe8e6] pt-4">
                      <p className="text-2xl font-semibold text-[#9d1f1c]">
                        {formatNumber(campaignDetails.donors)}
                      </p>
                      <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[#52615f]">
                        {t("donateForm.stats.donors")}
                      </p>
                    </div>
                    <div className="border-t border-[#dfe8e6] pt-4">
                      <p className="text-2xl font-semibold text-[#9d1f1c]">
                        {formatAmount(campaignDetails.achieved, amountPrefix, `${amountPrefix}0`)}
                      </p>
                      <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[#52615f]">
                        {t("donateForm.stats.raised")}
                      </p>
                    </div>
                    <div className="border-t border-[#dfe8e6] pt-4">
                      <p className="text-2xl font-semibold text-[#9d1f1c]">
                        {formatAmount(campaignDetails.goal, amountPrefix, t("donateForm.stats.flexible"))}
                      </p>
                      <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[#52615f]">
                        {t("donateForm.stats.goal")}
                      </p>
                    </div>
                    <div className="border-t border-[#dfe8e6] pt-4">
                      <p className="text-2xl font-semibold text-[#9d1f1c]">
                        {campaignDetails.daysLeft || t("donateForm.stats.ongoing")}
                      </p>
                      <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[#52615f]">
                        {t("donateForm.stats.daysLeft")}
                      </p>
                    </div>
                  </div>
                  {campaignDetails.gallery?.length > 0 && (
                    <div className="mt-7 border-t border-[#dfe8e6] pt-6">
                      <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-[#9d1f1c]">
                        {t("donateForm.impactInPictures")}
                      </h3>
                      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {campaignDetails.gallery.map((photo, index) => (
                          <button
                            key={`${photo.url}-${index}`}
                            type="button"
                            onClick={() => setLightbox(photo)}
                            className="group overflow-hidden rounded-lg border border-[#dfe8e6] text-left"
                          >
                            <img
                              src={photo.url}
                              alt={photo.caption || `${campaignDetails.title} impact ${index + 1}`}
                              loading="lazy"
                              className="aspect-[4/3] w-full object-cover transition group-hover:scale-105"
                              onError={(event) => {
                                event.currentTarget.closest("button").style.display = "none";
                              }}
                            />
                            {photo.caption && (
                              <span className="block px-2 py-1.5 text-xs leading-4 text-[#52615f]">
                                {photo.caption}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-[#dfe8e6] bg-white p-6 shadow-xl md:p-8">
              <div className="mb-7 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#9d1f1c]">
                    {t("donateForm.secureGiving")}
                  </p>
                  <h2 className="mt-3 text-3xl font-semibold text-[#14201f]">
                    {regionCopy.provider} {t("donateForm.checkout")}
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-[#52615f]">
                    {t("donateForm.completeDonationIn", { currency })}
                  </p>
                </div>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#f8efe5] text-[#9d1f1c]">
                  <ProviderIcon size={24} />
                </span>
              </div>

              <div
                role="group"
                aria-label={t("donateForm.donorRegionLabel")}
                className="mb-7 grid grid-cols-2 gap-2 rounded-lg bg-[#f1f6f4] p-2"
              >
                {["IN", "US"].map((key) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={activeRegion === key}
                    aria-label={key === "IN" ? t("donateForm.donateFromIndia") : t("donateForm.donateFromUSA")}
                    onClick={() => selectRegion(key)}
                    className={`rounded-md px-4 py-3 text-sm font-bold uppercase tracking-[0.12em] transition ${
                      activeRegion === key
                        ? "bg-white text-[#9d1f1c] shadow-sm"
                        : "text-[#52615f] hover:text-[#14201f]"
                    }`}
                  >
                    {key === "IN" ? t("donateForm.india") : t("donateForm.usa")}
                  </button>
                ))}
              </div>

              <div
                role="group"
                aria-label={t("donateForm.givingFrequencyLabel")}
                className="mb-5 grid grid-cols-2 gap-2 rounded-lg bg-[#f1f6f4] p-2"
              >
                {[
                  { key: "one-time", label: t("donateForm.giveOnce") },
                  { key: "monthly", label: t("donateForm.giveMonthly") },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={givingMode === key}
                    onClick={() => {
                      setGivingMode(key);
                      setValue("amount", "");
                    }}
                    className={`rounded-md px-4 py-3 text-sm font-bold tracking-[0.08em] transition ${
                      givingMode === key
                        ? "bg-white text-[#9d1f1c] shadow-sm"
                        : "text-[#52615f] hover:text-[#14201f]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
                <AmountSelector
                  tiers={activeTiers}
                  value={watchAmount || ""}
                  onTierSelect={(amt) =>
                    setValue("amount", amt, { shouldValidate: true, shouldTouch: true })
                  }
                  amountPrefix={amountPrefix}
                  currency={currency}
                  min={MIN_AMOUNT[activeRegion] ?? (isIndia ? 100 : 1)}
                  step={isIndia ? "1" : "0.01"}
                  registration={register("amount")}
                  error={errors.amount?.message}
                  inputId="donation-amount"
                />

                {/* Trust block — Option B: Secure · Receipt · Seva */}
                <div className="rounded-lg border border-[#dfe8e6] bg-[#f7faf9] p-4">
                  <div className="flex flex-wrap items-center gap-4 text-sm text-[#52615f]">
                    <span className="flex items-center gap-1.5">
                      <ShieldCheck className="h-4 w-4 text-[#9d1f1c]" />
                      {t("donateForm.trust.securePayment")}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-[#9d1f1c]" />
                      {t("donateForm.trust.receiptIssued")}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Heart className="h-4 w-4 text-[#9d1f1c]" />
                      {t("donateForm.trust.sevaSupported")}
                    </span>
                  </div>
                  <div className="mt-3">
                    <PaymentLogos region={activeRegion} />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field
                    id="donor-name"
                    label={t("donateForm.fields.fullName")}
                    hideLabel
                    required
                    type="text"
                    autoComplete="name"
                    placeholder={t("donateForm.fields.fullName")}
                    error={errors.fullName?.message}
                    registration={register("fullName")}
                  />
                  <Field
                    id="donor-email"
                    label={t("donateForm.fields.email")}
                    hideLabel
                    required
                    type="email"
                    autoComplete="email"
                    placeholder={t("donateForm.fields.email")}
                    error={errors.email?.message}
                    registration={register("email")}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field
                    id="donor-phone"
                    label={isIndia ? t("donateForm.fields.mobileNumber") : t("donateForm.fields.phoneNumber")}
                    hideLabel
                    required
                    type="tel"
                    autoComplete="tel"
                    placeholder={isIndia ? t("donateForm.fields.mobileNumber") : t("donateForm.fields.phoneNumber")}
                    error={errors.mobile?.message}
                    registration={register("mobile")}
                  />
                  <Field
                    id="donor-address"
                    label={isIndia ? t("donateForm.fields.cityAddress") : t("donateForm.fields.cityState")}
                    hideLabel
                    type="text"
                    autoComplete="address-level2"
                    placeholder={isIndia ? t("donateForm.fields.cityAddress") : t("donateForm.fields.cityState")}
                    error={errors.address?.message}
                    registration={register("address")}
                  />
                </div>

                {isIndia && (
                  <div className="rounded-lg border border-[#dfe8e6] bg-[#f7faf9] p-4">
                    <p className="text-sm font-semibold text-[#14201f]">
                      {t("donateForm.tax80GQuestion")}
                    </p>
                    <div className="mt-3 flex gap-5 text-sm text-[#52615f]">
                      {[
                        { value: "yes", label: t("donateForm.yes") },
                        { value: "no", label: t("donateForm.no") },
                      ].map(({ value: option, label }) => (
                        <label key={option} className="flex items-center gap-2">
                          <input
                            type="radio"
                            value={option}
                            checked={claim80G === option}
                            onChange={(event) => setClaim80G(event.target.value)}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>

                    {claim80G === "yes" && (
                      <div className="mt-4">
                        <Field
                          id="donor-pan"
                          label={t("donateForm.fields.panCard")}
                          hideLabel
                          required
                          type="text"
                          autoComplete="off"
                          placeholder={t("donateForm.fields.panCard")}
                          className="uppercase"
                          hint={t("donateForm.fields.panHint")}
                          error={errors.pan?.message}
                          registration={register("pan")}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label
                    htmlFor="donor-terms"
                    className="flex items-start gap-3 text-sm leading-6 text-[#52615f]"
                  >
                    <input
                      id="donor-terms"
                      type="checkbox"
                      aria-invalid={errors.termsAccepted ? "true" : undefined}
                      aria-describedby={errors.termsAccepted ? "donor-terms-error" : undefined}
                      aria-required="true"
                      className="mt-1 rounded border-[#dfe8e6] text-[#9d1f1c] focus:ring-[#9d1f1c]/20"
                      {...register("termsAccepted")}
                    />
                    <span>
                      {t("donateForm.termsConfirmation")}
                    </span>
                  </label>
                  {errors.termsAccepted && (
                    <p
                      id="donor-terms-error"
                      className="mt-2 flex items-center gap-2 text-sm text-red-600"
                    >
                      <AlertCircle size={15} aria-hidden="true" />
                      {errors.termsAccepted.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isProcessing || (isIndia && !isRazorpayLoaded)}
                  aria-busy={isProcessing}
                  className="flex w-full items-center justify-center gap-3 rounded-md bg-[#9d1f1c] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#7f1916] disabled:cursor-not-allowed disabled:bg-[#a7b2af]"
                >
                  {isProcessing ? <Loader className="h-5 w-5 animate-spin" /> : <Heart size={18} />}
                  {givingMode === "monthly" ? (
                    t("donateForm.startMonthlySeva")
                  ) : (
                    <>
                      {t("donateForm.donate")}{" "}
                      {parseAmount(watchAmount)
                        ? `${amountPrefix}${parseAmount(watchAmount).toLocaleString(
                            isIndia ? "en-IN" : "en-US"
                          )}`
                        : `${t("donateForm.inCurrency")} ${currency}`}
                    </>
                  )}
                  <ArrowRight size={18} />
                </button>
              </form>

              <div className="mt-6 flex gap-3 rounded-lg bg-[#f1f6f4] p-4 text-sm leading-6 text-[#52615f]">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#9d1f1c]" />
                <p>
                  {isIndia
                    ? t("donateForm.trustNote.india")
                    : t("donateForm.trustNote.usa")}
                </p>
              </div>
            </div>
          </div>
        )}
      </section>
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="max-h-full max-w-3xl" onClick={(event) => event.stopPropagation()}>
            <img
              src={lightbox.url}
              alt={lightbox.caption || campaignDetails.title}
              className="max-h-[80vh] w-full rounded-lg object-contain"
            />
            {lightbox.caption && (
              <p className="mt-3 text-center text-sm text-white/90">{lightbox.caption}</p>
            )}
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="mx-auto mt-4 block rounded-md bg-white/15 px-4 py-2 text-sm font-medium text-white hover:bg-white/25"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default DonationForm;
